import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useIntl } from 'react-intl';
import * as XLSX from 'xlsx';
import { api } from '../../services/api-client';
import { useToast } from '../../contexts/ToastContext';
import {
  TEMPLATE_COLUMNS,
  buildExampleRows,
  buildCatalogBlocks,
  layoutCatalogSheet,
  toSheetRows,
  ICatalogItem,
  ITemplateCatalogs
} from '../../utils/exerciseTemplate';

interface Props {
  visible: boolean;
  onHide: () => void;
  onPickFile: () => void;
}

interface ICatalogRequest {
  key: keyof ITemplateCatalogs;
  fetcher: () => Promise<{ data?: ICatalogItem[] }>;
}

/**
 * `Promise.allSettled` sobre los 10 catálogos: uno caído no tumba la descarga,
 * la hoja de referencia sale sin ese bloque y el resto del archivo sirve igual.
 *
 * La clave viaja DENTRO de cada promesa en vez de aparearse después por índice:
 * así el resultado se arma sin indexar dos arrays en paralelo.
 */
async function fetchCatalogs(): Promise<Partial<ITemplateCatalogs>> {
  const pedidos: ICatalogRequest[] = [
    { key: 'categories', fetcher: api.exercise.fetchCategories },
    { key: 'variants', fetcher: api.exercise.fetchVariants },
    { key: 'contractions', fetcher: api.exercise.fetchContractions },
    { key: 'difficulties', fetcher: api.exercise.fetchDifficulties },
    { key: 'movementPlanes', fetcher: api.exercise.fetchMovementPlanes },
    { key: 'unilateralTypes', fetcher: api.exercise.fetchUnilateralTypes },
    { key: 'movementPatterns', fetcher: api.exercise.fetchMovementPatterns },
    { key: 'equipments', fetcher: api.exercise.fetchEquipments },
    { key: 'muscles', fetcher: api.exercise.fetchMuscles },
    { key: 'rpeMethods', fetcher: api.exercise.fetchRpeMethods }
  ];

  const resultados = await Promise.allSettled(
    pedidos.map(async ({ key, fetcher }) => ({ key, data: (await fetcher()).data ?? [] }))
  );

  const catalogs: Partial<ITemplateCatalogs> = {};
  resultados.forEach((resultado) => {
    if (resultado.status === 'fulfilled') {
      catalogs[resultado.value.key] = resultado.value.data;
    }
  });
  return catalogs;
}

export default function ImportExercisesHelpDialog({ visible, onHide, onPickFile }: Props) {
  const intl = useIntl();
  const { showToast } = useToast();
  const [downloading, setDownloading] = useState(false);

  /**
   * Arma el xlsx con dos hojas: los ejemplos y la lista de valores aceptados.
   *
   * Los valores salen del catálogo del coach, no de una lista escrita a mano: el
   * import resuelve los catálogos por nombre y **descarta en silencio** el que no
   * encuentra, así que una plantilla con nombres que su base no tiene produce
   * ejercicios a medias sin un solo mensaje de error.
   */
  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const catalogs = await fetchCatalogs();
      const headerByKey: Record<string, string> = {};
      TEMPLATE_COLUMNS.forEach((c) => {
        headerByKey[c.key] = intl.formatMessage({ id: c.headerId });
      });
      const headers = TEMPLATE_COLUMNS.map((c) => headerByKey[c.key] as string);

      const hojaEjercicios = XLSX.utils.json_to_sheet(toSheetRows(buildExampleRows(catalogs), headerByKey), {
        header: headers
      });
      hojaEjercicios['!cols'] = headers.map((h) => ({ wch: Math.max(h.length + 2, 22) }));

      // Hoja 2: una tabla por catálogo, con id y nombre, puestas lado a lado para
      // que cada una sea un rango contiguo al que apuntarle un BUSCARV.
      const bloques = layoutCatalogSheet(buildCatalogBlocks(catalogs), {
        id: intl.formatMessage({ id: 'coach.importExercises.help.sheet.values.id' }),
        name: intl.formatMessage({ id: 'coach.importExercises.help.sheet.values.name' }),
        multiHint: intl.formatMessage({ id: 'coach.importExercises.help.sheet.values.multi' }),
        headerByKey
      });
      const hojaValores = XLSX.utils.aoa_to_sheet([[]]);
      bloques.forEach((bloque) => {
        XLSX.utils.sheet_add_aoa(hojaValores, bloque.rows, { origin: { r: 0, c: bloque.startColumn } });
      });
      hojaValores['!cols'] = bloques.flatMap(() => [{ wch: 6 }, { wch: 34 }, { wch: 2 }]);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        hojaEjercicios,
        intl.formatMessage({ id: 'coach.importExercises.help.sheet.exercises' })
      );
      if (bloques.length > 0) {
        XLSX.utils.book_append_sheet(
          wb,
          hojaValores,
          intl.formatMessage({ id: 'coach.importExercises.help.sheet.values' })
        );
      } else {
        // Sin catálogos la hoja de referencia quedaría vacía y los ejemplos traen
        // los nombres del seed, que pueden no coincidir con la base del coach.
        showToast(
          'warn',
          intl.formatMessage({ id: 'coach.importExercises.help.templatePartial.title' }),
          intl.formatMessage({ id: 'coach.importExercises.help.templatePartial' })
        );
      }

      XLSX.writeFile(wb, 'plantilla-ejercicios.xlsx');
    } catch (error) {
      showToast(
        'error',
        intl.formatMessage({ id: 'coach.importExercises.help.templateError.title' }),
        (error as Error).message
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      header={intl.formatMessage({ id: 'coach.importExercises.help.title' })}
      visible={visible}
      onHide={onHide}
      draggable={false}
      resizable={false}
      dismissableMask
      style={{ width: '40rem', maxWidth: '92vw' }}
    >
      <div className="flex flex-column gap-3">
        <p className="m-0" style={{ color: 'var(--ios-text-secondary)' }}>
          {intl.formatMessage({ id: 'coach.importExercises.help.intro' })}
        </p>

        <div
          style={{
            background: 'var(--ios-surface-subtle)',
            border: '1px solid var(--ios-card-border)',
            borderRadius: 'var(--ios-radius-md)',
            padding: '0.75rem 1rem'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ios-text-secondary)' }}>
                <th style={{ padding: '0.35rem 0.5rem' }}>
                  {intl.formatMessage({ id: 'coach.importExercises.help.table.column' })}
                </th>
                <th style={{ padding: '0.35rem 0.5rem' }}>
                  {intl.formatMessage({ id: 'coach.importExercises.help.table.description' })}
                </th>
                <th style={{ padding: '0.35rem 0.5rem' }}>
                  {intl.formatMessage({ id: 'coach.importExercises.help.table.required' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {TEMPLATE_COLUMNS.map((c) => (
                <tr key={c.key} style={{ borderTop: '1px solid var(--ios-divider)' }}>
                  <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>
                    {intl.formatMessage({ id: c.headerId })}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{intl.formatMessage({ id: c.descriptionId })}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>
                    {c.required ? intl.formatMessage({ id: 'common.yes' }) : intl.formatMessage({ id: 'common.no' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-content-end gap-2 flex-wrap">
          <Button
            label={intl.formatMessage({ id: 'coach.importExercises.help.downloadTemplate' })}
            icon="pi pi-download"
            className="p-button-outlined"
            loading={downloading}
            onClick={downloadTemplate}
          />
          <Button
            label={intl.formatMessage({ id: 'coach.importExercises.help.chooseFile' })}
            icon="pi pi-upload"
            onClick={() => {
              onPickFile();
              onHide();
            }}
          />
        </div>
      </div>
    </Dialog>
  );
}
